import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getToken } from '../utils/auth';
import { Eye, EyeOff } from 'lucide-react';

const PasswordInput = React.memo(({ name, value, label, show, toggleShow, inputRef, onChange }) => {
  return (
    <div className="mb-6">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type={show ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-10 outline-none"
          placeholder="Enter your password"
          autoComplete="off"
        />
      <button
        type="button"
        onClick={toggleShow}
        onMouseDown={(e) => e.preventDefault()}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded p-1"
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? (
          <Eye className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
        ) : (
          <EyeOff className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
        )}
      </button>
    </div>
  </div>
  );
});

PasswordInput.displayName = 'PasswordInput';

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Refs to maintain focus on inputs
  const currentPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    const inputElement = e.target;
    
    // Store cursor position and focus state BEFORE state update
    const cursorPosition = inputElement.selectionStart || 0;
    const isFocused = document.activeElement === inputElement;
    
    // Use functional update to ensure we're working with latest state
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      return newData;
    });
    
    // Clear error using functional update to avoid dependency
    setError(prevError => prevError ? null : prevError);
    
    // Restore focus and cursor position after state update
    if (isFocused) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        const refMap = {
          currentPassword: currentPasswordRef,
          newPassword: newPasswordRef,
          confirmPassword: confirmPasswordRef
        };
        const ref = refMap[name];
        if (ref && ref.current) {
          // Focus the input
          ref.current.focus();
          // Restore cursor position (account for the new character)
          const newPosition = Math.min(cursorPosition + 1, ref.current.value.length);
          ref.current.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
    }
  }, []);

  // Stable toggle functions that preserve focus
  const toggleShowCurrent = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPasswords(prev => ({ ...prev, current: !prev.current }));
    // Maintain focus on input after toggle
    setTimeout(() => {
      if (currentPasswordRef.current) {
        currentPasswordRef.current.focus();
        // Move cursor to end
        const length = currentPasswordRef.current.value.length;
        currentPasswordRef.current.setSelectionRange(length, length);
      }
    }, 0);
  }, []);

  const toggleShowNew = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPasswords(prev => ({ ...prev, new: !prev.new }));
    setTimeout(() => {
      if (newPasswordRef.current) {
        newPasswordRef.current.focus();
        const length = newPasswordRef.current.value.length;
        newPasswordRef.current.setSelectionRange(length, length);
      }
    }, 0);
  }, []);

  const toggleShowConfirm = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }));
    setTimeout(() => {
      if (confirmPasswordRef.current) {
        confirmPasswordRef.current.focus();
        const length = confirmPasswordRef.current.value.length;
        confirmPasswordRef.current.setSelectionRange(length, length);
      }
    }, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to change password' }));
        throw new Error(errorData.detail || 'Failed to change password');
      }

      await response.json();

      setSuccess(true);
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      setTimeout(() => {
        navigate('/mydashboard');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere">
      <Header />
      <main className="flex-grow w-full max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Change Password</h1>
          <p className="text-gray-600">Update your account password to keep your account secure</p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          {success ? (
            <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-lg text-center">
              <p className="font-semibold mb-1">Password Changed Successfully!</p>
              <p className="text-sm">Redirecting you to dashboard...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}

              <PasswordInput
                key="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                label="Current Password"
                show={showPasswords.current}
                toggleShow={toggleShowCurrent}
                inputRef={currentPasswordRef}
                onChange={handleInputChange}
              />

              <PasswordInput
                key="newPassword"
                name="newPassword"
                value={formData.newPassword}
                label="New Password"
                show={showPasswords.new}
                toggleShow={toggleShowNew}
                inputRef={newPasswordRef}
                onChange={handleInputChange}
              />

              <div className="mb-6 text-sm text-gray-600">
                Password must be at least 8 characters long
              </div>

              <PasswordInput
                key="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                label="Confirm New Password"
                show={showPasswords.confirm}
                toggleShow={toggleShowConfirm}
                inputRef={confirmPasswordRef}
                onChange={handleInputChange}
              />

              <div className="flex gap-3 pt-6 border-t">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating Password...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex-1 bg-gray-200 text-gray-900 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">Password Tips</h3>
            <ul className="space-y-1 text-sm text-blue-800">
              <li>• Use a mix of uppercase and lowercase letters</li>
              <li>• Include numbers and special characters</li>
              <li>• Avoid using personal information</li>
              <li>• Don't reuse passwords from other accounts</li>
            </ul>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ChangePasswordPage;

